import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UGCLayout from '@/components/layouts/UGCLayout';
import NavigationBar from '@/components/ui/NavigationBar';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';

const UGCSettings = () => {
  const navigate = useNavigate();
  const { user, profile, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    fullName: profile?.full_name || '',
    email: user?.email || '',
  });

  const ugcNavItems = [
    { path: '/app/ugc-dashboard', label: 'Dashboard', icon: 'Layout' },
    { path: '/app/ugc-marketplace', label: 'Marketplace', icon: 'ShoppingBag' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  const ugcMoreItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
    { path: '/app/ugc-media', label: 'Media Library', icon: 'Film' },
  ];

  const tabs = [
    { id: 'account', label: 'Account', icon: 'User' },
    { id: 'profile', label: 'Creator Profile', icon: 'Edit2' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
  ];

  const handleSaveAccount = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        full_name: formData.fullName,
      });
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearMediaLibrary = async () => {
    if (confirm('This will delete all your recorded videos from the media library. This action cannot be undone. Continue?')) {
      try {
        localStorage.removeItem('ugc_video_library');
        localStorage.removeItem('ugc_intro_done');
        setMessage({ type: 'success', text: 'Media library cleared' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to clear media library' });
      }
    }
  };

  return (
    <UGCLayout>
      <NavigationBar items={ugcNavItems} moreItems={ugcMoreItems} hideLocks homePath="/app/ugc-dashboard" />
      <main className="max-w-5xl mx-auto px-4 py-8 mb-20">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage your UGC creator account and preferences</p>
        </div>

        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg border ${
              message.type === 'success'
                ? 'bg-green-500/10 border-green-500/30 text-green-200'
                : 'bg-red-500/10 border-red-500/30 text-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-primary/10 text-primary'
                  : 'bg-white/5 text-muted-foreground hover:bg-white/10'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Account Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Full Name</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-white placeholder-muted-foreground focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-muted-foreground cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Email cannot be changed here. Contact support to update.</p>
                </div>
              </div>
              <Button className="mt-6" onClick={handleSaveAccount} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}

        {/* Creator Profile Tab */}
        {activeTab === 'profile' && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Creator Profile</h3>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Your creator profile settings, portfolio links, and brand preferences are coming soon. You'll be able to customize how brands see you on the marketplace.
                </p>
                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <p className="text-sm font-medium text-white mb-2">Features coming soon:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Portfolio/demo reel links</li>
                    <li>Niches and specialties</li>
                    <li>Pricing preferences</li>
                    <li>Availability calendar</li>
                    <li>Social media links</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Notification Preferences</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/5">
                  <div>
                    <p className="font-medium text-white">New Bids</p>
                    <p className="text-sm text-muted-foreground">Get notified when someone bids on your deals</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4" />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/5">
                  <div>
                    <p className="font-medium text-white">Bid Accepted</p>
                    <p className="text-sm text-muted-foreground">Get notified when your bid is accepted</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4" />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/5">
                  <div>
                    <p className="font-medium text-white">Submission Updates</p>
                    <p className="text-sm text-muted-foreground">Get notified about submission status changes</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4" />
                </div>
              </div>
              <Button className="mt-6" disabled>
                Notifications coming soon
              </Button>
            </div>

            {/* Danger Zone */}
            <div className="border-t border-white/10 pt-6">
              <h3 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h3>
              <Button
                variant="ghost"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/30"
                onClick={handleClearMediaLibrary}
              >
                <Icon name="Trash2" size={16} className="mr-2" />
                Clear Media Library
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                This will permanently delete all your recorded videos from the media library.
              </p>
            </div>
          </div>
        )}
      </main>
    </UGCLayout>
  );
};

export default UGCSettings;
